import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useAutoRefresh } from "@/hooks/useAutoRefresh";
import { MagnifyingGlass, FileArrowDown, FileXls, FilePdf, Funnel, X, Plus, Trash, PencilSimple } from "@phosphor-icons/react";
import ContractEditModal, { canEditContract } from "@/components/ContractEditModal";
import { toast } from "sonner";

const fmtBRL = (v) => (v || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL", maximumFractionDigits: 2 });

const STATUS_COLORS = {
  ativo: { bg: "#10B98120", text: "#10B981" },
  encerrado: { bg: "#94A3B820", text: "#94A3B8" },
  vencido: { bg: "#EF444420", text: "#EF4444" },
  em_analise: { bg: "#F59E0B20", text: "#F59E0B" },
  suspenso: { bg: "#F59E0B20", text: "#F59E0B" },
};

export default function Contracts() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [items, setItems] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [editingContract, setEditingContract] = useState(null);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [munis, setMunis] = useState([]);
  const [facets, setFacets] = useState({ modalidades: [], statuses: [] });
  const [anos, setAnos] = useState([]);
  const [filters, setFilters] = useState({
    search: "", municipio_id: "", status: "", modalidade: "", fornecedor: "",
    min_valor: "", max_valor: "", data_inicio_de: "", data_inicio_ate: "", ano: "",
    skip: 0, limit: 25,
  });

  useEffect(() => {
    api.get("/municipalities").then((r) => setMunis(r.data));
    api.get("/contracts/facets").then((r) => setFacets(r.data));
    api.get("/dashboard/anos-disponiveis").then((r) => setAnos(r.data.anos || []));
  }, []);

  const fetchContracts = () => {
    const params = Object.fromEntries(Object.entries(filters).filter(([_, v]) => v !== "" && v !== null));
    return api.get("/contracts", { params })
      .then((r) => { setItems(r.data.items); setTotal(r.data.total); });
  };

  useEffect(() => {
    setLoading(true);
    fetchContracts().finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  useAutoRefresh(fetchContracts, 30000);

  const reset = () => setFilters({ search: "", municipio_id: "", status: "", modalidade: "", fornecedor: "",
                                    min_valor: "", max_valor: "", data_inicio_de: "", data_inicio_ate: "", ano: "",
                                    skip: 0, limit: 25 });

  const exportFile = async (kind) => {
    try {
      const params = new URLSearchParams();
      if (filters.municipio_id) params.set("municipio_id", filters.municipio_id);
      if (filters.status) params.set("status", filters.status);
      const res = await fetch(`${API}/export/${kind}?${params.toString()}`, { credentials: "include" });
      if (!res.ok) throw new Error("Erro ao exportar");
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = kind === "excel" ? "contratos.xlsx" : "contratos.pdf";
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Exportação concluída");
    } catch (e) {
      toast.error("Falha na exportação");
    }
  };

  const deleteContract = async (c) => {
    if (!window.confirm(`Excluir o contrato ${c.numero}?\n\nEsta ação também removerá:\n• Todos os aditivos vinculados\n• Todas as execuções financeiras\n• Notificações e relatórios PDF arquivados\n\nNão é possível desfazer.`)) return;
    try {
      const { data } = await api.delete(`/contracts/${c.id}`);
      const d = data?.deleted || {};
      toast.success(`Contrato ${c.numero} excluído (${d.aditivos || 0} aditivos, ${d.execucoes || 0} execuções, ${d.relatorios || 0} relatórios)`);
      setItems((prev) => prev.filter((x) => x.id !== c.id));
      setTotal((t) => Math.max(0, t - 1));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao excluir");
    }
  };

  const renderTableBody = () => {
    if (loading) return <tr><td colSpan={9} className="px-5 py-10 text-center text-[#94A3B8]">Carregando…</td></tr>;
    if (items.length === 0) return <tr><td colSpan={9} className="px-5 py-10 text-center text-[#94A3B8]">Nenhum contrato encontrado.</td></tr>;
    return items.map((c, i) => {
      const sc = STATUS_COLORS[c.status] || STATUS_COLORS.ativo;
      return (
        <tr key={c.id} data-testid={`row-contract-${i}`}
            className="border-t border-[#334155] hover:bg-[#1E293B]/60 transition-colors">
          <td className="px-5 py-4">
            <Link to={`/contratos/${c.id}`} className="font-mono text-[#F59E0B] hover:text-[#FCD34D]">{c.numero}</Link>
          </td>
          <td className="px-5 py-4 max-w-[280px] truncate">{c.objeto}</td>
          <td className="px-5 py-4 text-[#94A3B8]">{c.municipio_nome}</td>
          <td className="px-5 py-4 text-[#94A3B8] max-w-[180px] truncate">{c.fornecedor}</td>
          <td className="px-5 py-4 text-xs text-[#94A3B8]">{c.modalidade}</td>
          <td className="px-5 py-4 text-right font-mono">{fmtBRL(c.valor)}</td>
          <td className="px-5 py-4 text-xs text-[#94A3B8]">{c.data_inicio?.slice(0,10)} → {c.data_fim?.slice(0,10)}</td>
          <td className="px-5 py-4">
            <span className="px-2.5 py-1 rounded-full text-xs font-medium" style={{ background: sc.bg, color: sc.text }}>
              {c.status}
            </span>
          </td>
          <td className="px-5 py-4 text-right whitespace-nowrap">
            {canEditContract(c, user) && (
              <button
                data-testid={`row-edit-${i}`}
                onClick={() => setEditingContract(c)}
                title="Editar dados do contrato"
                className="p-1.5 rounded text-[#94A3B8] hover:text-[#F59E0B] hover:bg-[#F59E0B]/10 transition-colors"
              >
                <PencilSimple size={14} weight="duotone" />
              </button>
            )}
            <button
              data-testid={`row-delete-${i}`}
              onClick={() => deleteContract(c)}
              title="Excluir contrato"
              className="p-1.5 rounded text-[#94A3B8] hover:text-[#EF4444] hover:bg-[#EF4444]/10 transition-colors"
            >
              <Trash size={14} weight="duotone" />
            </button>
          </td>
        </tr>
      );
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <div className="uppercase-label">Base de contratos</div>
          <h1 className="font-heading text-4xl tracking-tight mt-2">Contratos</h1>
          <p className="text-sm text-[#94A3B8] mt-2">
            {total.toLocaleString("pt-BR")} registros encontrados
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="export-excel" onClick={() => exportFile("excel")}
                  className="px-4 py-2.5 rounded-md border border-[#334155] hover:border-[#F59E0B] text-sm flex items-center gap-2 transition-colors">
            <FileXls size={16} /> Excel
          </button>
          <button data-testid="export-pdf" onClick={() => exportFile("pdf")}
                  className="px-4 py-2.5 rounded-md border border-[#334155] hover:border-[#F59E0B] text-sm flex items-center gap-2 transition-colors">
            <FilePdf size={16} /> PDF
          </button>
          <button data-testid="new-contract-button" onClick={() => setShowCreate(true)}
                  className="px-4 py-2.5 rounded-md bg-[#F59E0B] text-[#0F172A] hover:bg-[#FCD34D] text-sm font-medium flex items-center gap-2">
            <Plus size={16} weight="bold" /> Novo contrato
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="tce-card p-6">
        <div className="flex items-center gap-2 mb-5 text-[#F59E0B]">
          <Funnel size={16} weight="duotone" />
          <span className="uppercase-label !text-[#F59E0B]">Filtros</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
          <div className="md:col-span-2">
            <div className="relative">
              <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
              <input
                data-testid="search-input"
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value, skip: 0 })}
                placeholder="Buscar por número, objeto ou fornecedor…"
                className="tce-input w-full pl-10 pr-4 py-2.5 rounded-md text-sm"
              />
            </div>
          </div>
          <select data-testid="filter-municipio" value={filters.municipio_id}
                  onChange={(e) => setFilters({ ...filters, municipio_id: e.target.value, skip: 0 })}
                  disabled={!isAdmin}
                  className="tce-input px-3 py-2.5 rounded-md text-sm disabled:opacity-60 disabled:cursor-not-allowed">
            <option value="">{isAdmin ? "Todos os municípios" : (user?.municipio_nome || "Seu município")}</option>
            {munis.map((m) => <option key={m.ibge_code} value={m.ibge_code}>{m.name}</option>)}
          </select>
          <select data-testid="filter-status" value={filters.status}
                  onChange={(e) => setFilters({ ...filters, status: e.target.value, skip: 0 })}
                  className="tce-input px-3 py-2.5 rounded-md text-sm">
            <option value="">Todos os status</option>
            {facets.statuses.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <select data-testid="filter-modalidade" value={filters.modalidade}
                  onChange={(e) => setFilters({ ...filters, modalidade: e.target.value, skip: 0 })}
                  className="tce-input px-3 py-2.5 rounded-md text-sm">
            <option value="">Todas as modalidades</option>
            {facets.modalidades.map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
          <input data-testid="filter-fornecedor" placeholder="Fornecedor/Fiscal"
                 value={filters.fornecedor}
                 onChange={(e) => setFilters({ ...filters, fornecedor: e.target.value, skip: 0 })}
                 className="tce-input px-3 py-2.5 rounded-md text-sm" />
          <input data-testid="filter-min-valor" type="number" placeholder="Valor mínimo (R$)"
                 value={filters.min_valor}
                 onChange={(e) => setFilters({ ...filters, min_valor: e.target.value, skip: 0 })}
                 className="tce-input px-3 py-2.5 rounded-md text-sm" />
          <select data-testid="filter-ano" value={filters.ano}
                  onChange={(e) => setFilters({ ...filters, ano: e.target.value, skip: 0 })}
                  className="tce-input px-3 py-2.5 rounded-md text-sm">
            <option value="">Todos os anos</option>
            {anos.map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
          <input data-testid="filter-max-valor" type="number" placeholder="Valor máximo (R$)"
                 value={filters.max_valor}
                 onChange={(e) => setFilters({ ...filters, max_valor: e.target.value, skip: 0 })}
                 className="tce-input px-3 py-2.5 rounded-md text-sm" />
          <div>
            <label className="uppercase-label block mb-1">Início de</label>
            <input data-testid="filter-data-de" type="date"
                   value={filters.data_inicio_de}
                   onChange={(e) => setFilters({ ...filters, data_inicio_de: e.target.value, skip: 0 })}
                   className="tce-input w-full px-3 py-2 rounded-md text-sm" />
          </div>
          <div>
            <label className="uppercase-label block mb-1">Início até</label>
            <input data-testid="filter-data-ate" type="date"
                   value={filters.data_inicio_ate}
                   onChange={(e) => setFilters({ ...filters, data_inicio_ate: e.target.value, skip: 0 })}
                   className="tce-input w-full px-3 py-2 rounded-md text-sm" />
          </div>
          <button data-testid="reset-filters" onClick={reset}
                  className="px-4 py-2.5 rounded-md border border-[#334155] hover:border-[#F59E0B] text-sm flex items-center gap-2 justify-center transition-colors">
            <X size={14} /> Limpar filtros
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="tce-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#1E293B]">
              <tr className="text-left">
                <th className="px-5 py-4 uppercase-label">Número</th>
                <th className="px-5 py-4 uppercase-label">Objeto</th>
                <th className="px-5 py-4 uppercase-label">Município</th>
                <th className="px-5 py-4 uppercase-label">Fornecedor</th>
                <th className="px-5 py-4 uppercase-label">Modalidade</th>
                <th className="px-5 py-4 uppercase-label text-right">Valor</th>
                <th className="px-5 py-4 uppercase-label">Vigência</th>
                <th className="px-5 py-4 uppercase-label">Status</th>
                <th className="px-5 py-4 uppercase-label text-right">Ações</th>
              </tr>
            </thead>
            <tbody>
              {renderTableBody()}
            </tbody>
          </table>
        </div>
        {/* Pagination */}
        <div className="flex items-center justify-between px-5 py-4 border-t border-[#334155] text-sm text-[#94A3B8]">
          <span>Página {Math.floor(filters.skip / filters.limit) + 1} de {Math.max(1, Math.ceil(total / filters.limit))}</span>
          <div className="flex gap-2">
            <button data-testid="page-prev" disabled={filters.skip === 0}
                    onClick={() => setFilters({ ...filters, skip: Math.max(0, filters.skip - filters.limit) })}
                    className="px-3 py-1.5 rounded border border-[#334155] hover:border-[#F59E0B] disabled:opacity-30">
              Anterior
            </button>
            <button data-testid="page-next" disabled={filters.skip + filters.limit >= total}
                    onClick={() => setFilters({ ...filters, skip: filters.skip + filters.limit })}
                    className="px-3 py-1.5 rounded border border-[#334155] hover:border-[#F59E0B] disabled:opacity-30">
              Próxima
            </button>
          </div>
        </div>
      </div>

      {editingContract && (
        <ContractEditModal
          contract={editingContract}
          onClose={() => setEditingContract(null)}
          onSaved={(updated) => {
            setEditingContract(null);
            setItems((prev) => prev.map((x) => (x.id === updated.id ? updated : x)));
          }}
        />
      )}

      {showCreate && (
        <NewContractModal
          munis={munis}
          isAdmin={isAdmin}
          userMuni={user?.municipio_id}
          userMuniName={user?.municipio_nome}
          facets={facets}
          onClose={() => setShowCreate(false)}
          onSaved={() => { setShowCreate(false); setFilters({ ...filters }); }}
        />
      )}
    </div>
  );
}

function useNewContractForm({ munis, isAdmin, userMuni, userMuniName, facets, onSaved }) {
  const [form, setForm] = useState({
    numero: "", objeto: "", fornecedor: "", cnpj_fornecedor: "",
    modalidade: facets.modalidades[0] || "Pregão Eletrônico",
    valor: "", data_inicio: "", data_fim: "",
    status: "ativo",
    municipio_id: isAdmin ? "" : (userMuni || ""),
    municipio_nome: isAdmin ? "" : (userMuniName || ""),
    orgao: "",
  });
  const [itens, setItens] = useState([]);  // [{numero_item,descricao,quantidade,unidade_medida,valor_unitario,valor_total}]
  const [saving, setSaving] = useState(false);
  const [orgaos, setOrgaos] = useState([]);

  useEffect(() => {
    if (!form.municipio_id) { setOrgaos([]); return; }
    let cancelled = false;
    api.get(`/municipalities/${form.municipio_id}/orgaos`)
      .then((r) => { if (!cancelled) setOrgaos(r.data?.orgaos || []); })
      .catch(() => { if (!cancelled) setOrgaos([]); });
    return () => { cancelled = true; };
  }, [form.municipio_id]);

  const addItem = () => {
    setItens((prev) => [...prev, {
      numero_item: prev.length + 1,
      descricao: "", quantidade: 1, unidade_medida: "UN",
      valor_unitario: 0, valor_total: 0,
    }]);
  };
  const updateItem = (idx, field, value) => {
    setItens((prev) => prev.map((it, i) => {
      if (i !== idx) return it;
      const next = { ...it, [field]: value };
      if (field === "quantidade" || field === "valor_unitario") {
        const q = parseFloat(field === "quantidade" ? value : next.quantidade) || 0;
        const vu = parseFloat(field === "valor_unitario" ? value : next.valor_unitario) || 0;
        next.valor_total = q * vu;
      }
      return next;
    }));
  };
  const removeItem = (idx) => setItens((prev) => prev.filter((_, i) => i !== idx)
    .map((it, i) => ({ ...it, numero_item: i + 1 })));
  const totalItens = itens.reduce((s, it) => s + (parseFloat(it.valor_total) || 0), 0);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const itensPayload = itens.filter((it) => it.descricao && it.descricao.trim())
        .map((it) => ({
          numero_item: it.numero_item,
          descricao: it.descricao.trim(),
          quantidade: parseFloat(it.quantidade) || 0,
          unidade_medida: it.unidade_medida || "UN",
          valor_unitario: parseFloat(it.valor_unitario) || 0,
          valor_total: parseFloat(it.valor_total) || 0,
        }));
      const payload = {
        ...form,
        valor: parseFloat(form.valor),
        itens: itensPayload.length ? itensPayload : null,
      };
      if (isAdmin) {
        const m = munis.find((mm) => mm.ibge_code === form.municipio_id);
        if (m) payload.municipio_nome = m.name;
      }
      await api.post("/contracts", payload);
      toast.success(itensPayload.length
        ? `Contrato cadastrado com ${itensPayload.length} item(ns)`
        : "Contrato cadastrado");
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao salvar");
    } finally { setSaving(false); }
  };

  return { form, setForm, saving, orgaos, submit, itens, addItem, updateItem, removeItem, totalItens, setItens };
}

function NewContractModal({ munis, isAdmin, userMuni, userMuniName, facets, onClose, onSaved }) {
  const { form, setForm, saving, orgaos, submit, itens, addItem, updateItem, removeItem, totalItens, setItens } = useNewContractForm({
    munis, isAdmin, userMuni, userMuniName, facets, onSaved,
  });
  const fileRef = useRef(null);

  const onCsvFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const text = await file.text();
      const sep = text.includes(";") && text.split("\n")[0].includes(";") ? ";" : ",";
      const lines = text.split(/\r?\n/).filter((l) => l.trim());
      if (lines.length < 2) { toast.error("CSV vazio ou sem dados"); return; }
      const headers = lines[0].split(sep).map((h) => h.trim().toLowerCase());
      const idx = {
        descricao: headers.indexOf("descricao"),
        quantidade: headers.indexOf("quantidade"),
        unidade_medida: headers.indexOf("unidade_medida"),
        valor_unitario: headers.indexOf("valor_unitario"),
        valor_total: headers.indexOf("valor_total"),
      };
      if (idx.descricao < 0 || idx.valor_unitario < 0) {
        toast.error("CSV deve ter colunas: descricao, valor_unitario (obrigatórias)");
        return;
      }
      const novos = lines.slice(1).map((line, i) => {
        const cols = line.split(sep);
        const desc = (cols[idx.descricao] || "").trim();
        if (!desc) return null;
        const qtd = parseFloat((cols[idx.quantidade] || "1").replace(",", ".")) || 1;
        const vu = parseFloat((cols[idx.valor_unitario] || "0").replace(",", ".")) || 0;
        const vt = idx.valor_total >= 0
          ? parseFloat((cols[idx.valor_total] || "0").replace(",", ".")) || qtd * vu
          : qtd * vu;
        return {
          numero_item: i + 1, descricao: desc, quantidade: qtd,
          unidade_medida: idx.unidade_medida >= 0 ? (cols[idx.unidade_medida] || "UN").trim() : "UN",
          valor_unitario: vu, valor_total: vt,
        };
      }).filter(Boolean);
      if (!novos.length) { toast.error("Nenhum item válido no CSV"); return; }
      setItens(novos);
      toast.success(`${novos.length} item(ns) importados do CSV`);
    } catch (err) {
      toast.error("Erro ao processar CSV: " + (err.message || "formato inválido"));
    }
    e.target.value = "";
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="tce-card w-full max-w-4xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-5 border-b border-[#334155]">
          <div>
            <div className="uppercase-label">Cadastro manual</div>
            <div className="font-heading text-xl mt-1">Novo contrato</div>
          </div>
          <button onClick={onClose} className="text-[#94A3B8] hover:text-[#F59E0B]"><X size={20} /></button>
        </div>
        <form onSubmit={submit} className="px-6 py-5 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="uppercase-label block mb-2">Número</label>
              <input data-testid="nc-numero" required value={form.numero}
                     onChange={(e) => setForm({ ...form, numero: e.target.value })}
                     placeholder="Ex: 0123/2026"
                     className="tce-input w-full px-3 py-2.5 rounded-md text-sm" />
            </div>
            <div>
              <label className="uppercase-label block mb-2">Modalidade</label>
              <select data-testid="nc-modalidade" value={form.modalidade}
                      onChange={(e) => setForm({ ...form, modalidade: e.target.value })}
                      className="tce-input w-full px-3 py-2.5 rounded-md text-sm">
                {facets.modalidades.map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="uppercase-label block mb-2">Objeto do contrato</label>
            <textarea data-testid="nc-objeto" required rows={2} value={form.objeto}
                      onChange={(e) => setForm({ ...form, objeto: e.target.value })}
                      className="tce-input w-full px-3 py-2.5 rounded-md text-sm" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="uppercase-label block mb-2">Fornecedor</label>
              <input data-testid="nc-fornecedor" required value={form.fornecedor}
                     onChange={(e) => setForm({ ...form, fornecedor: e.target.value })}
                     className="tce-input w-full px-3 py-2.5 rounded-md text-sm" />
            </div>
            <div>
              <label className="uppercase-label block mb-2">CNPJ/CPF</label>
              <input data-testid="nc-cnpj" value={form.cnpj_fornecedor}
                     onChange={(e) => setForm({ ...form, cnpj_fornecedor: e.target.value })}
                     placeholder="00.000.000/0000-00"
                     className="tce-input w-full px-3 py-2.5 rounded-md text-sm font-mono" />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="uppercase-label block mb-2">Valor total (R$)</label>
              <input data-testid="nc-valor" type="number" step="0.01" required value={form.valor}
                     onChange={(e) => setForm({ ...form, valor: e.target.value })}
                     className="tce-input w-full px-3 py-2.5 rounded-md text-sm font-mono" />
            </div>
            <div>
              <label className="uppercase-label block mb-2">Início vigência</label>
              <input data-testid="nc-inicio" type="date" required value={form.data_inicio}
                     onChange={(e) => setForm({ ...form, data_inicio: e.target.value })}
                     className="tce-input w-full px-3 py-2.5 rounded-md text-sm" />
            </div>
            <div>
              <label className="uppercase-label block mb-2">Fim vigência</label>
              <input data-testid="nc-fim" type="date" required value={form.data_fim}
                     onChange={(e) => setForm({ ...form, data_fim: e.target.value })}
                     className="tce-input w-full px-3 py-2.5 rounded-md text-sm" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="uppercase-label block mb-2">Município</label>
              {isAdmin ? (
                <select data-testid="nc-muni" required value={form.municipio_id}
                        onChange={(e) => setForm({ ...form, municipio_id: e.target.value })}
                        className="tce-input w-full px-3 py-2.5 rounded-md text-sm">
                  <option value="">Selecione…</option>
                  {munis.map((m) => <option key={m.ibge_code} value={m.ibge_code}>{m.name}</option>)}
                </select>
              ) : (
                <input value={userMuniName || ""} disabled
                       className="tce-input w-full px-3 py-2.5 rounded-md text-sm opacity-60" />
              )}
            </div>
            <div>
              <label className="uppercase-label block mb-2">Órgão / Secretaria</label>
              <input data-testid="nc-orgao" value={form.orgao}
                     onChange={(e) => setForm({ ...form, orgao: e.target.value })}
                     list="orgao-options"
                     placeholder={form.municipio_id ? "Escolha ou digite…" : "Selecione um município primeiro"}
                     disabled={!form.municipio_id}
                     className="tce-input w-full px-3 py-2.5 rounded-md text-sm disabled:opacity-50" />
              <datalist id="orgao-options">
                {orgaos.map((o) => <option key={o} value={o} />)}
              </datalist>
            </div>
          </div>

          {/* Editor de itens do contrato */}
          <div className="border-t border-[#334155] pt-4">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <div>
                <div className="uppercase-label !text-[#F59E0B]">Itens do contrato (opcional)</div>
                <p className="text-[11px] text-[#94A3B8] mt-0.5">
                  Permite pesquisa de preços item-a-item conforme IN 65/2021. Soma calculada: <strong className="text-[#FCD34D] font-mono">{totalItens.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })}</strong>
                </p>
              </div>
              <div className="flex items-center gap-2">
                <a href={`${API}/contracts/itens/template-csv`}
                   data-testid="nc-csv-template"
                   className="px-3 py-1.5 rounded-md border border-[#334155] hover:border-[#10B981] text-xs flex items-center gap-1.5 text-[#10B981]"
                   title="Baixar planilha-modelo (.csv) com cabeçalho e exemplos">
                  📥 Planilha-modelo
                </a>
                <input ref={fileRef} type="file" accept=".csv" onChange={onCsvFile} className="hidden"
                       data-testid="nc-csv-input" />
                <button type="button" onClick={() => fileRef.current?.click()}
                        data-testid="nc-csv-btn"
                        className="px-3 py-1.5 rounded-md border border-[#334155] hover:border-[#F59E0B] text-xs flex items-center gap-1.5">
                  Importar CSV
                </button>
                <button type="button" onClick={addItem}
                        data-testid="nc-add-item"
                        className="px-3 py-1.5 rounded-md border border-[#F59E0B]/40 text-[#F59E0B] hover:bg-[#F59E0B]/10 text-xs flex items-center gap-1.5">
                  + Adicionar item
                </button>
              </div>
            </div>
            {itens.length === 0 && (
              <div className="text-[11px] text-[#94A3B8] italic py-2 text-center border border-dashed border-[#334155] rounded">
                Nenhum item lançado. O contrato será criado apenas com objeto+valor global.
              </div>
            )}
            {itens.length > 0 && (
              <div className="space-y-2">
                <div className="grid grid-cols-12 gap-2 text-[10px] uppercase text-[#94A3B8] px-1">
                  <div className="col-span-1">#</div>
                  <div className="col-span-5">Descrição</div>
                  <div className="col-span-1">Qtde</div>
                  <div className="col-span-1">Unid</div>
                  <div className="col-span-2 text-right">Vl. unit (R$)</div>
                  <div className="col-span-2 text-right">Total (R$)</div>
                </div>
                {itens.map((it, idx) => (
                  <div key={idx} className="grid grid-cols-12 gap-2 items-center" data-testid={`nc-item-row-${idx}`}>
                    <div className="col-span-1 text-xs text-[#94A3B8] font-mono">{it.numero_item}</div>
                    <input value={it.descricao}
                           onChange={(e) => updateItem(idx, "descricao", e.target.value)}
                           placeholder="Ex: Vassoura de pelo"
                           className="col-span-5 tce-input px-2 py-1.5 rounded text-xs" />
                    <input type="number" step="0.01" value={it.quantidade}
                           onChange={(e) => updateItem(idx, "quantidade", e.target.value)}
                           className="col-span-1 tce-input px-2 py-1.5 rounded text-xs font-mono" />
                    <input value={it.unidade_medida}
                           onChange={(e) => updateItem(idx, "unidade_medida", e.target.value)}
                           className="col-span-1 tce-input px-2 py-1.5 rounded text-xs" />
                    <input type="number" step="0.01" value={it.valor_unitario}
                           onChange={(e) => updateItem(idx, "valor_unitario", e.target.value)}
                           className="col-span-2 tce-input px-2 py-1.5 rounded text-xs font-mono text-right" />
                    <div className="col-span-1 text-xs font-mono text-right text-[#FCD34D]">
                      {(parseFloat(it.valor_total) || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}
                    </div>
                    <button type="button" onClick={() => removeItem(idx)}
                            data-testid={`nc-item-del-${idx}`}
                            className="col-span-1 text-[#94A3B8] hover:text-[#EF4444] text-xs">
                      <X size={14} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="flex justify-end gap-2 pt-2 border-t border-[#334155]">
            <button type="button" onClick={onClose}
                    className="px-4 py-2 rounded-md border border-[#334155] hover:border-[#94A3B8] text-sm">
              Cancelar
            </button>
            <button data-testid="nc-save" disabled={saving}
                    className="px-5 py-2 rounded-md bg-[#F59E0B] text-[#0F172A] hover:bg-[#FCD34D] font-medium text-sm disabled:opacity-50">
              {saving ? "Salvando…" : "Cadastrar contrato"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
